/*
 * HUM‑ID Sensor‑Kalkulator – v17
 * – Alle bisherigen Features + Kopieren/Drehen/Löschen via Rechtsklick-Kontextmenü
 *   im "Auswahl verschieben"-Modus
 */

import { useState, useRef, useEffect } from "react";
import { jsPDF } from "jspdf";
import {
  Stage,
  Layer,
  Line,
  Image as KonvaImage,
  Circle,
  Group,
  Rect,
} from "react-konva";
import "./style.css";
import * as pdfjsLib from "pdfjs-dist/build/pdf";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const MAX_IMG_WIDTH = 3000;
const JPG_QUALITY = 0.92;
const TARGET_IMG_MAX_H = 900;

/* ──────────────────────────────────────────────────────────────
 * FEATURE FLAGS
 * Auf `true` setzen, um ein Feature im Frontend wieder freizuschalten.
 * Die zugehörige Logik (State, Handler, Undo, Reset, Rendering)
 * bleibt im Code erhalten und wird durch den Flag aktiviert.
 * ────────────────────────────────────────────────────────────── */
const AURA_FEATURE_ENABLED = false;

/* ---- SVG‑Platzhalter (Info‑Icon) ---- */
const InfoIcon = (props) => (
  <svg width="24" height="24" viewBox="0 0 24 24" {...props}>
    <circle cx="12" cy="12" r="11" stroke="white" strokeWidth="2" fill="none" />
    <line x1="12" y1="8" x2="12" y2="13" stroke="white" strokeWidth="2" />
    <circle cx="12" cy="17" r="1" fill="white" />
  </svg>
);

export default function SensorKalkulationDummy() {
  /* -------------------- State -------------------- */
  const containerRef = useRef(null);
  const [step, setStep] = useState(1);
  const [fileName, setFileName] = useState("");
  const [imageObj, setImageObj] = useState(null);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [showStep5Info, setShowStep5Info] = useState(false); // Info-Popover für Schritt 5
  const handleCloseOfferModal = () => {
    setShowOfferModal(false);
  };

  const [showPolygonHelpPopover, setShowPolygonHelpPopover] = useState(false);
  const [dontShowPolygonHelpAgain, setDontShowPolygonHelpAgain] = useState(
    localStorage.getItem("dontShowPolygonHelpAgain") === "true"
  );

  const restrictDragBounds = (pos) => {
    let newX = pos.x;
    let newY = pos.y;

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 0;
    const stageWrapper = containerRef.current;
    const containerHeight = stageWrapper ? stageWrapper.offsetHeight : 0;

    if (!imageObj || containerWidth === 0 || containerHeight === 0) {
      return pos;
    }

    newY = Math.min(newY, 0);
    newX = Math.min(newX, 0);

    const scaledImageHeight = imageObj.height * stageScale;
    if (scaledImageHeight > containerHeight) {
      newY = Math.max(newY, containerHeight - scaledImageHeight);
    }

    const scaledImageWidth = imageObj.width * stageScale;
    if (scaledImageWidth > containerWidth) {
      newX = Math.max(newX, containerWidth - scaledImageWidth);
    }

    return { x: newX, y: newY };
  };

  const [mPerPx, setMPerPx] = useState(null);
  const [realDistance, setRealDistance] = useState("");
  const [scalePoints, setScalePoints] = useState([]);

  const [polygonPoints, setPolygonPoints] = useState([]);
  const [polyClosed, setPolyClosed] = useState(false);
  const [areaM2, setAreaM2] = useState(null);
  const [sensorRaster, setSensorRaster] = useState("1");
  const [sensorCount, setSensorCount] = useState(null);
  const [sensorPoints, setSensorPoints] = useState([]);

  const [stageScale, setStageScale] = useState(1);
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 });
  const stageRef = useRef(null);
  const [history, setHistory] = useState([]);

  // Raster‑Drag
  const [rasterOffset, setRasterOffset] = useState({ x: 0, y: 0 });
  const [rasterDraggable, setRasterDraggable] = useState(false);

  // Sensor‑Delete‑Modus
  const [deleteMode, setDeleteMode] = useState(false);
  const [deletedIds, setDeletedIds] = useState([]);

  // Punkte‑Hinzufügen
  const [addMode, setAddMode] = useState(false);
  const [addedPoints, setAddedPoints] = useState([]);

  // Mehrfach‑Löschen (Bereich)
  const [areaDeleteMode, setAreaDeleteMode] = useState(false);
  const [selRect, setSelRect] = useState(null);

  // Mehrfach‑Auswahl & Verschieben
  const [moveSelectionMode, setMoveSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [sensorOffsets, setSensorOffsets] = useState({});

  // Aura (blaue Reichweite, 10 m)
  const [auraMode, setAuraMode] = useState(false);
  const [auraPoints, setAuraPoints] = useState([]);

  // ────────────────── Kontextmenü & Kopieren/Drehen ──────────────────
  const [contextMenu, setContextMenu] = useState(null); // {screenX, screenY, imgX, imgY}
  const [clipboard, setClipboard] = useState(null); // [{x,y}, ...] relative Punkte
  const [pasteMode, setPasteMode] = useState(false); // Warte auf Rechtsklick zum Einfügen
  const [rotateMode, setRotateMode] = useState(false); // Dreh-Modus aktiv
  const [rotateAngle, setRotateAngle] = useState(0); // aktueller Winkel in Rad
  const [rotateCenter, setRotateCenter] = useState(null); // {x,y} Mittelpunkt

  /* ────────── Undo-History (nur Schritt 5) ────────── */

  const undoLastAction = () => {
    if (history.length === 0) return;
    const last = history[history.length - 1];

    setHistory((h) => h.slice(0, -1));

    switch (last.type) {
      case "drag":
        setRasterOffset(last.prevOffset);
        break;

      case "add":
        setAddedPoints((pts) => pts.slice(0, -1));
        break;

      case "delete":
        setDeletedIds((ids) =>
          ids.filter((id) => !last.idsDeleted.includes(id))
        );
        break;

      case "moveSelection":
        setSensorOffsets((prev) => {
          const next = { ...prev };
          last.ids.forEach((id) => {
            const cur = next[id] || { x: 0, y: 0 };
            next[id] = { x: cur.x - last.dx, y: cur.y - last.dy };
          });
          return next;
        });
        break;

      case "auraAdd":
        setAuraPoints((pts) => pts.filter((a) => a.id !== last.aura.id));
        break;

      case "auraDelete":
        setAuraPoints((pts) => [...pts, last.aura]);
        break;

      case "auraMove":
        setAuraPoints((pts) =>
          pts.map((p) =>
            p.id === last.id ? { ...p, x: last.prevX, y: last.prevY } : p
          )
        );
        break;

      case "paste":
        setAddedPoints((pts) => pts.slice(0, -last.count));
        break;

      case "rotate":
        setSensorOffsets((prev) => {
          const nxt = { ...prev };
          last.ids.forEach((id) => {
            nxt[id] = last.prevOffsets[id] || { x: 0, y: 0 };
          });
          return nxt;
        });
        break;

      default:
        console.warn("Unbekannter Undo-Typ:", last);
    }
  };

  /* ---------- Schritt‑bezogene Info‑Texte ---------- */
  const stepHelp = {
    1: "Wählen Sie eine PDF‑, PNG‑ oder JPG‑Datei (Dachaufsicht o. ä.) aus.",
    2: "Klicken Sie zwei Punkte eines bekannten Maßstabs an und geben Sie die Distanz ein.",
    3: "Klicken Sie nacheinander die Eckpunkte der Fläche. Mit Klick auf den ersten Punkt schließen Sie das Polygon.",
    4: "Wählen Sie das gewünschte Sensor‑Raster. Empfehlung: 1×1 m bei < 200 m² …",
    5: "Raster verschieben, Sensoren löschen/hinzufügen. Anschließend PDF herunterladen.",
  };

  // Logo vorladen
  const [logoImg, setLogoImg] = useState(null);
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = `https://www.hum-id.com/new/logo-website-big-01.png`;
    img.onload = () => setLogoImg(img);
    img.onerror = () => console.warn("Logo konnte nicht vorgeladen werden");
  }, []);

  useEffect(() => {
    if (step === 3 && !dontShowPolygonHelpAgain && polygonPoints.length === 0) {
      setShowPolygonHelpPopover(true);
    } else {
      setShowPolygonHelpPopover(false);
    }
  }, [step, dontShowPolygonHelpAgain, polygonPoints.length]);

  const handleClosePolygonHelpPopover = () => {
    setShowPolygonHelpPopover(false);
  };

  const handleDontShowPolygonHelpAgain = () => {
    localStorage.setItem("dontShowPolygonHelpAgain", "true");
    setDontShowPolygonHelpAgain(true);
    setShowPolygonHelpPopover(false);
  };

  /* ---------------- Upload ---------------- */
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);

    let dataUrl;

    if (file.type === "application/pdf") {
      dataUrl = await pdfToPngDataUrl(file);
    } else {
      dataUrl = URL.createObjectURL(file);
    }

    const img = new Image();
    img.onload = () => setImageObj(img);
    img.src = dataUrl;
  };

  /* ------------- Zoom / Pan -------------- */
  const toImage = (pos) => ({
    x: (pos.x - stagePos.x) / stageScale,
    y: (pos.y - stagePos.y) / stageScale,
  });

  const handleWheel = (e) => {
    e.evt.preventDefault();
    const scaleBy = 1.05;
    const old = stageScale;
    const stage = e.target.getStage();
    const ptr = stage.getPointerPosition();
    const mouse = {
      x: (ptr.x - stagePos.x) / old,
      y: (ptr.y - stagePos.y) / old,
    };
    const newScale = e.evt.deltaY > 0 ? old / scaleBy : old * scaleBy;
    setStageScale(newScale);
    setStagePos({
      x: ptr.x - mouse.x * newScale,
      y: ptr.y - mouse.y * newScale,
    });
  };

  /* ────────── Kontextmenü: Kopieren / Drehen / Löschen ────────── */

  // Zentrum einer Punkt-Gruppe berechnen
  const getCenterOfPoints = (pts) => {
    if (!pts.length) return { x: 0, y: 0 };
    const sumX = pts.reduce((s, p) => s + p.x, 0);
    const sumY = pts.reduce((s, p) => s + p.y, 0);
    return { x: sumX / pts.length, y: sumY / pts.length };
  };

  // Ausgewählte Sensor-Objekte (mit Offset) sammeln
  const getSelectedSensorCoords = () => {
    const ptFor = (id) =>
      id.startsWith("s-")
        ? sensorPoints[+id.slice(2)]
        : addedPoints[+id.slice(2)];
    return selectedIds
      .filter((id) => !deletedIds.includes(id))
      .map((id) => {
        const base = ptFor(id);
        if (!base) return null;
        const off = sensorOffsets[id] || { x: 0, y: 0 };
        return {
          x: base.x + rasterOffset.x + off.x,
          y: base.y + rasterOffset.y + off.y,
        };
      })
      .filter(Boolean);
  };

  /* ── Kopieren ── */
  const handleCopy = () => {
    const coords = getSelectedSensorCoords();
    if (!coords.length) return;
    const center = getCenterOfPoints(coords);
    setClipboard(coords.map((p) => ({ x: p.x - center.x, y: p.y - center.y })));
    setPasteMode(true);
    setContextMenu(null);
  };

  /* ── Einfügen ── */
  const handlePaste = (imgX, imgY) => {
    if (!clipboard || !clipboard.length) return;
    const newPts = clipboard.map((p) => ({
      x: imgX + p.x - rasterOffset.x,
      y: imgY + p.y - rasterOffset.y,
    }));
    setAddedPoints((prev) => [...prev, ...newPts]);
    setHistory((h) => [...h, { type: "paste", count: newPts.length }]);
    setPasteMode(false);
    setContextMenu(null);
  };

  /* ── Drehen starten (öffnet Steuerungs-Panel) ── */
  const handleRotateStart = () => {
    const coords = getSelectedSensorCoords();
    if (!coords.length) return;
    const center = getCenterOfPoints(coords);
    setRotateCenter(center);
    setRotateAngle(0); // Grad (nicht Rad)
    setRotateMode(true);
    setContextMenu(null);
  };

  /* ── Drehen anwenden ── */
  const applyRotation = () => {
    if (!rotateCenter || rotateAngle === 0) {
      setRotateMode(false);
      return;
    }

    const angleRad = (rotateAngle * Math.PI) / 180; // Grad → Rad

    const ptFor = (id) =>
      id.startsWith("s-")
        ? sensorPoints[+id.slice(2)]
        : addedPoints[+id.slice(2)];

    const prevOffsets = {};
    setSensorOffsets((prev) => {
      const nxt = { ...prev };
      selectedIds.forEach((id) => {
        const base = ptFor(id);
        if (!base) return;
        const off = prev[id] || { x: 0, y: 0 };
        const absX = base.x + rasterOffset.x + off.x;
        const absY = base.y + rasterOffset.y + off.y;
        const dx = absX - rotateCenter.x;
        const dy = absY - rotateCenter.y;
        const newAbsX =
          rotateCenter.x + dx * Math.cos(angleRad) - dy * Math.sin(angleRad);
        const newAbsY =
          rotateCenter.y + dx * Math.sin(angleRad) + dy * Math.cos(angleRad);
        prevOffsets[id] = { ...(prev[id] || { x: 0, y: 0 }) };
        nxt[id] = {
          x: newAbsX - base.x - rasterOffset.x,
          y: newAbsY - base.y - rasterOffset.y,
        };
      });
      return nxt;
    });

    setHistory((h) => [
      ...h,
      {
        type: "rotate",
        ids: [...selectedIds],
        prevOffsets,
        center: { ...rotateCenter },
        angle: rotateAngle, // in Grad gespeichert
      },
    ]);
    setRotateMode(false);
    setRotateAngle(0);
  };

  /* ── Löschen (über Kontextmenü) ── */
  const handleDeleteSelected = () => {
    if (!selectedIds.length) return;
    setDeletedIds((old) => [...old, ...selectedIds]);
    setHistory((h) => [...h, { type: "delete", idsDeleted: [...selectedIds] }]);
    setSelectedIds([]);
    setContextMenu(null);
  };

  /* ────────── Rechtsklick (Kontextmenü) ────────── */
  const handleContextMenu = (e) => {
    e.evt.preventDefault();

    if (!moveSelectionMode) return;

    const stage = e.target.getStage();
    const ptr = stage.getPointerPosition();
    const imgPos = toImage(ptr);

    // Paste-Modus: Rechtsklick = Einfügen
    if (pasteMode && clipboard) {
      handlePaste(imgPos.x, imgPos.y);
      return;
    }

    // Auswahl vorhanden → Kontextmenü anzeigen
    if (selectedIds.length > 0) {
      setContextMenu({
        screenX: e.evt.clientX,
        screenY: e.evt.clientY,
        imgX: imgPos.x,
        imgY: imgPos.y,
      });
    }
  };

  /* ---------------- Clicks ---------------- */
  const handleCanvasClick = (e) => {
    if (e.evt.button !== 0 || e.evt.ctrlKey || e.evt.altKey || e.evt.shiftKey) {
      return;
    }

    // Kontextmenü schließen bei Linksklick
    if (contextMenu) {
      setContextMenu(null);
      return;
    }

    // Im Dreh-Modus keine Klick-Aktionen
    if (rotateMode) return;

    const p = toImage(e.target.getStage().getPointerPosition());

    /* Schritt 2 – Maßstabs­punkte */
    if (step === 2 && scalePoints.length < 4) {
      setScalePoints((prev) => [...prev, p.x, p.y]);
      return;
    }
    if (step === 3 && !polyClosed) {
      if (polygonPoints.length >= 4) {
        const [fx, fy] = polygonPoints;
        if (Math.hypot(p.x - fx, p.y - fy) < 8) {
          setPolyClosed(true);
          return;
        }
      }
      setPolygonPoints((prev) => [...prev, p.x, p.y]);
    }
    /* ───── Schritt 5: Sensorpunkt hinzufügen ───── */
    if (step === 5 && addMode) {
      if (pointInPoly(p.x, p.y)) {
        const newPt = { x: p.x - rasterOffset.x, y: p.y - rasterOffset.y };
        setAddedPoints((pts) => [...pts, newPt]);
        setHistory((h) => [...h, { type: "add" }]);
      }
      return;
    }

    /* ───── Schritt 5: Aura hinzufügen / löschen ───── */
    if (step === 5 && auraMode) {
      const targetName =
        e.target && typeof e.target.name === "function" ? e.target.name() : "";
      if (targetName && targetName.indexOf("aura-") === 0) {
        const id = targetName;
        const deletedAura = auraPoints.find((a) => a.id === id);
        if (deletedAura) {
          setAuraPoints((pts) => pts.filter((a) => a.id !== id));
          setHistory((h) => [...h, { type: "auraDelete", aura: deletedAura }]);
        }
        return;
      }
      const newAura = {
        id: "aura-" + Date.now() + "-" + Math.random().toString(36).slice(2, 8),
        x: p.x - rasterOffset.x,
        y: p.y - rasterOffset.y,
      };
      setAuraPoints((pts) => [...pts, newAura]);
      setHistory((h) => [...h, { type: "auraAdd", aura: newAura }]);
      return;
    }
  };

  /* ---------------- Helpers -------------- */
  const applyScale = () => {
    if (scalePoints.length !== 4) return;
    const px = Math.hypot(
      scalePoints[0] - scalePoints[2],
      scalePoints[1] - scalePoints[3]
    );
    const real = parseFloat(realDistance);
    if (!px || !real) return;
    setMPerPx(real / px);
    setStep(3);
  };

  const undoLast = () => !polyClosed && setPolygonPoints((p) => p.slice(0, -2));
  const resetPoly = () => {
    setPolygonPoints([]);
    setPolyClosed(false);
  };

  const undoScalePoint = () => setScalePoints((pts) => pts.slice(0, -2));

  const pointInPoly = (x, y) => {
    let inside = false;
    for (
      let i = 0, j = polygonPoints.length - 2;
      i < polygonPoints.length;
      i += 2
    ) {
      const xi = polygonPoints[i],
        yi = polygonPoints[i + 1];
      const xj = polygonPoints[j],
        yj = polygonPoints[j + 1];
      const intersect =
        yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
      j = i;
    }
    return inside;
  };

  const polyAreaPx = () => {
    if (!polyClosed || polygonPoints.length < 6) return 0;
    let a = 0;
    for (let i = 0; i < polygonPoints.length; i += 2) {
      const x1 = polygonPoints[i],
        y1 = polygonPoints[i + 1];
      const x2 = polygonPoints[(i + 2) % polygonPoints.length],
        y2 = polygonPoints[(i + 3) % polygonPoints.length];
      a += x1 * y2 - x2 * y1;
    }
    return Math.abs(a / 2);
  };

  const finalizePoly = () => {
    const areaPx = polyAreaPx();
    if (!areaPx || !mPerPx) return;
    setAreaM2(areaPx * mPerPx * mPerPx);
    setStep(4);
  };

  async function pdfToPngDataUrl(file) {
    const buffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;
    const page = await pdf.getPage(1);

    const baseViewport = page.getViewport({ scale: 1 });
    const scale = Math.min(MAX_IMG_WIDTH / baseViewport.width, 1);
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;

    await page.render({
      canvasContext: canvas.getContext("2d"),
      viewport,
    }).promise;

    return canvas.toDataURL("image/jpeg", JPG_QUALITY);
  }

  /* ---------- Sensor Points ---------- */
  const generateSensorPoints = () => {
    const spacingM = Math.sqrt(parseFloat(sensorRaster));
    const spacingPx = spacingM / mPerPx;
    const xs = polygonPoints.filter((_, i) => i % 2 === 0);
    const ys = polygonPoints.filter((_, i) => i % 2 === 1);
    const minX = Math.min(...xs),
      maxX = Math.max(...xs);
    const minY = Math.min(...ys),
      maxY = Math.max(...ys);
    const pts = [];
    for (let y = minY; y <= maxY; y += spacingPx) {
      for (let x = minX; x <= maxX; x += spacingPx) {
        if (pointInPoly(x, y)) pts.push({ x, y });
      }
    }
    return pts;
  };

  useEffect(() => {
    if (step === 5 && polyClosed && mPerPx) {
      const pts = generateSensorPoints();
      setSensorPoints(pts);
      setSensorCount(pts.length);
    }
  }, [step, sensorRaster]);

  useEffect(() => {
    if (!imageObj || !containerRef.current) return;

    const cw = containerRef.current.clientWidth;
    const ch = Math.min(TARGET_IMG_MAX_H, window.innerHeight - 180);
    const scaleToFit = Math.min(cw / imageObj.width, ch / imageObj.height);

    setStageScale(scaleToFit);
    setStagePos({
      x: (cw - imageObj.width * scaleToFit) / 2,
      y: (ch - imageObj.height * scaleToFit) / 2,
    });
  }, [imageObj]);

  /* ---------------- PDF ---------------- */
  const handleDownload = async () => {
    if (!stageRef.current) return;

    const firaSansRegularTTF = `https://www.hum-id.com/new/FiraSans-Regular.ttf`;
    const firaSansBoldTTF = `https://www.hum-id.com/new/FiraSans-Bold.ttf`;

    let firaSansRegularBase64 = null;
    let firaSansBoldBase64 = null;

    try {
      const responseRegular = await fetch(firaSansRegularTTF);
      if (!responseRegular.ok)
        throw new Error(
          `FiraSans-Regular.ttf nicht gefunden unter ${firaSansRegularTTF}`
        );
      const blobRegular = await responseRegular.blob();
      firaSansRegularBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blobRegular);
      });

      const responseBold = await fetch(firaSansBoldTTF);
      if (!responseBold.ok)
        throw new Error(
          `FiraSans-Bold.ttf nicht gefunden unter ${firaSansBoldTTF}`
        );
      const blobBold = await responseBold.blob();
      firaSansBoldBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blobBold);
      });
    } catch (error) {
      console.warn(
        "Fira Sans Schriftart konnte nicht geladen werden, verwende Standardschriftart:",
        error
      );
      // Kein alert() – PDF wird stillschweigend mit Helvetica erstellt
    }

    const stage = stageRef.current;
    const prevScale = stage.scaleX();
    const prevPos = { x: stage.x(), y: stage.y() };

    stage.scale({ x: 1, y: 1 });
    stage.position({ x: 0, y: 0 });
    stage.batchDraw();
    const pngDataUrl = stage.toDataURL({ pixelRatio: 1 });
    stage.scale({ x: prevScale, y: prevScale });
    stage.position(prevPos);
    stage.batchDraw();

    const jpegDataUrl = await new Promise((res) => {
      const img = new Image();
      img.onload = () => {
        const c = document.createElement("canvas");
        c.width = img.width;
        c.height = img.height;
        c.getContext("2d").drawImage(img, 0, 0);
        res(c.toDataURL("image/jpeg", 0.7));
      };
      img.src = pngDataUrl;
    });

    const doc = new jsPDF({
      unit: "mm",
      format: "a4",
      orientation: "l",
    });

    if (firaSansRegularBase64) {
      doc.addFileToVFS("FiraSans-Regular.ttf", firaSansRegularBase64);
      doc.addFont("FiraSans-Regular.ttf", "FiraSans", "normal");
      doc.setFont("FiraSans", "normal");
    } else {
      doc.setFont("Helvetica", "normal");
    }

    if (firaSansBoldBase64) {
      doc.addFileToVFS("FiraSans-Bold.ttf", firaSansBoldBase64);
      doc.addFont("FiraSans-Bold.ttf", "FiraSans", "bold");
    }

    const pageWjsPDF = doc.internal.pageSize.getWidth();
    const pageHjsPDF = doc.internal.pageSize.getHeight();

    doc.setFontSize(14);
    if (firaSansBoldBase64) {
      doc.setFont("FiraSans", "bold");
    }
    doc.text("HUM-ID Sensor-Plan", 10, 15);
    if (firaSansBoldBase64) {
      doc.setFont("FiraSans", "normal");
    }

    doc.setFontSize(10);
    const info = [
      `Quadratmeter der Sensorfläche: ${areaM2?.toFixed(1) || "-"}`,
      `HUM-ID Sensoren: ${
        sensorPoints.length + addedPoints.length - deletedIds.length
      }`,
      ...(AURA_FEATURE_ENABLED
        ? [`Aktive HUM-ID Auras: ${auraPoints.length}`]
        : []),
      `Raster: 1 Sensor pro ${sensorRaster} m²`,
    ].join(" | ");
    doc.text(info, 10, 20);

    if (logoImg) {
      const logoWmm = 25;
      const ratio = logoImg.height / logoImg.width;
      const logoHmm = logoWmm * ratio;
      const canvasLogo = document.createElement("canvas");
      canvasLogo.width = logoImg.width;
      canvasLogo.height = logoImg.height;
      const ctx = canvasLogo.getContext("2d");
      ctx.drawImage(logoImg, 0, 0);
      const logoDataUrl = canvasLogo.toDataURL("image/png");
      doc.addImage(
        logoDataUrl,
        "PNG",
        pageWjsPDF - logoWmm - 10,
        8,
        logoWmm,
        logoHmm
      );
    }

    const planImg = new Image();
    planImg.src = jpegDataUrl;
    await planImg.decode();

    const headerH = 45;
    const availH = pageHjsPDF - headerH;
    const availW = pageWjsPDF;

    let drawH = availH;
    let drawW = (planImg.width / planImg.height) * drawH;
    let x = (availW - drawW) / 2;
    let y = headerH;

    if (drawW > availW) {
      drawW = availW;
      drawH = (planImg.height / planImg.width) * drawW;
      x = 0;
    }
    doc.addImage(jpegDataUrl, "JPEG", x, y, drawW, drawH, undefined, "FAST");

    const footerMarginFromBottom = 10;
    const footerTextSize = 8;
    const footerY = pageHjsPDF - footerMarginFromBottom;

    doc.setFontSize(footerTextSize);

    const clientVersionText =
      "Client-Version, erstellt auf hum-id.com/sensorplaner";
    doc.text(clientVersionText, 10, footerY);

    const now = new Date();
    const day = String(now.getDate()).padStart(2, "0");
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const year = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");
    const dateTimeString = `${hours}:${minutes}:${seconds} ${day}/${month}/${year}`;

    const dateTimeStringWidth =
      (doc.getStringUnitWidth(dateTimeString) * footerTextSize) /
      doc.internal.scaleFactor;
    doc.text(dateTimeString, pageWjsPDF - dateTimeStringWidth - 10, footerY);

    doc.save("sensor_kalkulation.pdf");
    setShowOfferModal(true);
  };

  const toggleRasterDrag = () => setRasterDraggable((d) => !d);
  const toggleDeleteMode = () => setDeleteMode((d) => !d);
  const toggleAddMode = () => setAddMode((m) => !m);

  const resetAppStates = () => {
    setStep(1);
    setFileName("");
    setImageObj(null);

    setMPerPx(null);
    setRealDistance("");
    setScalePoints([]);

    setPolygonPoints([]);
    setPolyClosed(false);
    setAreaM2(null);

    setSensorRaster("1");

    setSensorCount(null);
    setSensorPoints([]);
    setRasterOffset({ x: 0, y: 0 });
    setRasterDraggable(false);
    setDeleteMode(false);
    setDeletedIds([]);
    setAddMode(false);
    setAddedPoints([]);
    setAreaDeleteMode(false);
    setSelRect(null);

    setMoveSelectionMode(false);
    setSelectedIds([]);
    setSensorOffsets({});

    setAuraMode(false);
    setAuraPoints([]);

    // Neue States zurücksetzen
    setContextMenu(null);
    setClipboard(null);
    setPasteMode(false);
    setRotateMode(false);
    setRotateAngle(0);
    setRotateCenter(null);
    setShowStep5Info(false);

    setHistory([]);

    setStageScale(1);
    setStagePos({ x: 0, y: 0 });

    const fileInput = document.querySelector('input[type="file"].file-input');
    if (fileInput) {
      fileInput.value = "";
    }
  };

  /* -------------- Components ------------ */
  const Btn = ({
    children,
    variant = "default",
    className = "",
    active = false,
    ...props
  }) => {
    return (
      <button
        className={`btn ${variant !== "default" ? variant : ""} ${
          active ? "btn-active" : ""
        } ${className}`}
        {...props}
      >
        {children}
      </button>
    );
  };

  // ───────── Mehrfach‑Lösch‑Rechteck Handler ─────────────────
  const handleRectStart = (e) => {
    if (!areaDeleteMode && !moveSelectionMode) return;
    // Im Dreh-Modus kein neues Rechteck starten
    if (rotateMode) return;

    if (moveSelectionMode) {
      const clickedName =
        e.target && typeof e.target.name === "function" ? e.target.name() : "";
      if (clickedName && clickedName.indexOf("selected-sensor") !== -1) {
        return;
      }
      setSelectedIds([]);
    }

    const { x, y } = toImage(e.target.getStage().getPointerPosition());
    setSelRect({ x, y, w: 0, h: 0 });
  };

  const handleRectMove = (e) => {
    if (rotateMode) return;
    if ((!areaDeleteMode && !moveSelectionMode) || !selRect) return;
    const { x, y } = toImage(e.target.getStage().getPointerPosition());
    setSelRect((r) => ({ ...r, w: x - r.x, h: y - r.y }));
  };

  const handleRectEnd = () => {
    if ((!areaDeleteMode && !moveSelectionMode) || !selRect) return;

    const x1 = Math.min(selRect.x, selRect.x + selRect.w);
    const x2 = Math.max(selRect.x, selRect.x + selRect.w);
    const y1 = Math.min(selRect.y, selRect.y + selRect.h);
    const y2 = Math.max(selRect.y, selRect.y + selRect.h);

    const isInside = (pt, id) => {
      const off = sensorOffsets[id] || { x: 0, y: 0 };
      const px = pt.x + rasterOffset.x + off.x;
      const py = pt.y + rasterOffset.y + off.y;
      return px >= x1 && px <= x2 && py >= y1 && py <= y2;
    };

    const allIds = [
      ...sensorPoints.map((_, i) => `s-${i}`),
      ...addedPoints.map((_, i) => `a-${i}`),
    ];
    const ptFor = (id) =>
      id.startsWith("s-")
        ? sensorPoints[+id.slice(2)]
        : addedPoints[+id.slice(2)];
    const hits = allIds.filter(
      (id) => !deletedIds.includes(id) && isInside(ptFor(id), id)
    );

    if (areaDeleteMode) {
      setDeletedIds((old) => [...old, ...hits]);
      setHistory((h) => [...h, { type: "delete", idsDeleted: hits }]);
    } else if (moveSelectionMode) {
      setSelectedIds(hits);
    }
    setSelRect(null);
  };

  /* ─── Berechne gedrehte Positionen für die Vorschau während des Drehens ─── */
  const getRotatedOffset = (id) => {
    if (!rotateMode || !rotateCenter || rotateAngle === 0) return null;
    if (!selectedIds.includes(id)) return null;

    const angleRad = (rotateAngle * Math.PI) / 180;

    const ptFor = (pid) =>
      pid.startsWith("s-")
        ? sensorPoints[+pid.slice(2)]
        : addedPoints[+pid.slice(2)];
    const base = ptFor(id);
    if (!base) return null;
    const off = sensorOffsets[id] || { x: 0, y: 0 };
    const absX = base.x + rasterOffset.x + off.x;
    const absY = base.y + rasterOffset.y + off.y;
    const dx = absX - rotateCenter.x;
    const dy = absY - rotateCenter.y;
    const newAbsX =
      rotateCenter.x + dx * Math.cos(angleRad) - dy * Math.sin(angleRad);
    const newAbsY =
      rotateCenter.y + dx * Math.sin(angleRad) + dy * Math.cos(angleRad);
    return {
      x: newAbsX - rasterOffset.x - base.x,
      y: newAbsY - rasterOffset.y - base.y,
    };
  };

  const ZoomStage = ({ showSensors = false }) => {
    if (!imageObj) return <p>Bild wird geladen…</p>;
    return (
      <Stage
        width={imageObj.width}
        height={imageObj.height}
        x={stagePos.x}
        y={stagePos.y}
        scaleX={stageScale}
        scaleY={stageScale}
        draggable
        dragBoundFunc={restrictDragBounds}
        onMouseDown={handleRectStart}
        onMouseMove={handleRectMove}
        onMouseUp={handleRectEnd}
        onWheel={handleWheel}
        onClick={handleCanvasClick}
        onContextMenu={handleContextMenu}
        onDragEnd={(e) => {
          if (e.target === stageRef.current) {
            const st = stageRef.current;
            setStagePos({ x: st.x(), y: st.y() });
          }
        }}
        ref={stageRef}
      >
        <Layer>
          <KonvaImage image={imageObj} />

          {/* Measure */}
          {step === 2 && scalePoints.length >= 2 && (
            <Circle
              x={scalePoints[0]}
              y={scalePoints[1]}
              radius={4}
              fill="#FF6363"
            />
          )}
          {step === 2 && scalePoints.length === 4 && (
            <>
              <Circle
                x={scalePoints[2]}
                y={scalePoints[3]}
                radius={4}
                fill="#FF6363"
              />
              <Line points={scalePoints} stroke="#00A3FF" strokeWidth={2} />
            </>
          )}

          {/* Polygon */}
          {step >= 3 && (
            <>
              <Line
                points={polygonPoints}
                closed={polyClosed}
                stroke="#00A3FF"
                strokeWidth={2}
                fill={polyClosed ? "rgba(46,204,113,0.25)" : undefined}
              />
              {polygonPoints.map(
                (_, i) =>
                  i % 2 === 0 && (
                    <Circle
                      key={i}
                      x={polygonPoints[i]}
                      y={polygonPoints[i + 1]}
                      radius={4}
                      fill="#FF6363"
                    />
                  )
              )}
            </>
          )}

          {/* Auswahl‑Rechteck */}
          {(areaDeleteMode || moveSelectionMode) && selRect && (
            <Rect
              x={selRect.x}
              y={selRect.y}
              width={selRect.w}
              height={selRect.h}
              stroke={moveSelectionMode ? "#0066ff" : "#E55400"}
              strokeWidth={2}
              dash={[6, 4]}
              opacity={0.8}
              fill={
                moveSelectionMode
                  ? "rgba(0,102,255,0.12)"
                  : "rgba(229,84,0,0.15)"
              }
            />
          )}

          {/* Sensors – in einer verschiebbaren Group */}
          <Group
            x={rasterOffset.x}
            y={rasterOffset.y}
            draggable={
              rasterDraggable && !deleteMode && !addMode && !moveSelectionMode
            }
            onDragEnd={(e) => {
              if (!rasterDraggable) return;
              const prev = { ...rasterOffset };
              const next = { x: e.target.x(), y: e.target.y() };
              setRasterOffset(next);
              setHistory((h) => [...h, { type: "drag", prevOffset: prev }]);
              setRasterDraggable(false);
            }}
          >
            {showSensors &&
              (() => {
                const SENSOR_COLOR = "#A5C418";
                const allPts = [
                  ...sensorPoints.map((p, i) => ({ ...p, id: `s-${i}` })),
                  ...addedPoints.map((p, i) => ({ ...p, id: `a-${i}` })),
                ].filter((pt) => !deletedIds.includes(pt.id));

                const unselected = allPts.filter(
                  (pt) => !selectedIds.includes(pt.id)
                );
                const selected = allPts.filter((pt) =>
                  selectedIds.includes(pt.id)
                );

                const renderSensor = (pt, isSel) => {
                  // Im Dreh-Modus: gedrehte Position für Vorschau verwenden
                  const rotOff = getRotatedOffset(pt.id);
                  const off = rotOff || sensorOffsets[pt.id] || { x: 0, y: 0 };
                  const cx = pt.x + off.x;
                  const cy = pt.y + off.y;
                  const cursor = deleteMode
                    ? "pointer"
                    : addMode
                    ? "crosshair"
                    : moveSelectionMode
                    ? "move"
                    : "default";
                  const sensorName = isSel
                    ? `selected-sensor sensor-${pt.id}`
                    : `sensor-${pt.id}`;
                  return (
                    <Group
                      key={pt.id}
                      x={cx}
                      y={cy}
                      name={sensorName}
                      onClick={
                        deleteMode
                          ? () => setDeletedIds((ids) => [...ids, pt.id])
                          : undefined
                      }
                    >
                      <Circle
                        radius={10}
                        fill={SENSOR_COLOR}
                        opacity={0.5}
                        name={sensorName}
                        cursor={cursor}
                      />
                      <Circle
                        radius={4.5}
                        fill={SENSOR_COLOR}
                        stroke={isSel ? "#0066ff" : undefined}
                        strokeWidth={isSel ? 1.5 : 0}
                        name={sensorName}
                        cursor={cursor}
                      />
                    </Group>
                  );
                };

                return (
                  <>
                    {unselected.map((pt) => renderSensor(pt, false))}
                    {selected.length > 0 && !rotateMode && (
                      <Group
                        name="selected-sensor selectedGroup"
                        draggable={moveSelectionMode}
                        onDragEnd={(e) => {
                          const dx = e.target.x();
                          const dy = e.target.y();
                          e.target.position({ x: 0, y: 0 });
                          if (dx === 0 && dy === 0) return;
                          const ids = [...selectedIds];
                          setSensorOffsets((prev) => {
                            const nxt = { ...prev };
                            ids.forEach((id) => {
                              const cur = nxt[id] || { x: 0, y: 0 };
                              nxt[id] = {
                                x: cur.x + dx,
                                y: cur.y + dy,
                              };
                            });
                            return nxt;
                          });
                          setHistory((h) => [
                            ...h,
                            { type: "moveSelection", ids, dx, dy },
                          ]);
                        }}
                      >
                        {selected.map((pt) => renderSensor(pt, true))}
                      </Group>
                    )}
                    {/* Im Dreh-Modus: Sensoren einzeln rendern (nicht in draggable Group) */}
                    {selected.length > 0 &&
                      rotateMode &&
                      selected.map((pt) => renderSensor(pt, true))}
                  </>
                );
              })()}

            {/* ─── Aura-Punkte ─── */}
            {AURA_FEATURE_ENABLED &&
              showSensors &&
              auraPoints.map((a) => {
                const AURA_COLOR = "#00B2FF";
                const mpp = mPerPx || 0.01;
                const outerRadiusPx = 10 / mpp;
                const midRadiusPx = 3 / mpp;
                const cursor = auraMode ? "pointer" : "move";
                const canDragAura =
                  !rasterDraggable &&
                  !areaDeleteMode &&
                  !addMode &&
                  !moveSelectionMode;
                return (
                  <Group
                    key={a.id}
                    x={a.x}
                    y={a.y}
                    draggable={canDragAura}
                    onDragEnd={(e) => {
                      const newX = e.target.x();
                      const newY = e.target.y();
                      if (newX === a.x && newY === a.y) return;
                      setAuraPoints((pts) =>
                        pts.map((p) =>
                          p.id === a.id ? { ...p, x: newX, y: newY } : p
                        )
                      );
                      setHistory((h) => [
                        ...h,
                        {
                          type: "auraMove",
                          id: a.id,
                          prevX: a.x,
                          prevY: a.y,
                        },
                      ]);
                    }}
                  >
                    <Circle
                      radius={outerRadiusPx}
                      fill={AURA_COLOR}
                      opacity={0.15}
                      listening={false}
                    />
                    <Circle
                      radius={midRadiusPx}
                      fill={AURA_COLOR}
                      opacity={0.35}
                      listening={false}
                    />
                    <Circle
                      radius={6}
                      fill={AURA_COLOR}
                      name={a.id}
                      cursor={cursor}
                      listening={canDragAura}
                    />
                  </Group>
                );
              })}
          </Group>
        </Layer>
      </Stage>
    );
  };

  /* ---------------- Render ---------------- */
  return (
    <>
      {/* ─── HUM-ID Webseite Badge ─────── */}
      <a
        href="https://www.hum-id.com"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          position: "fixed",
          top: "8px",
          right: "12px",
          zIndex: 9999,
          background: "#A5C418",
          color: "#fff",
          padding: "6px 12px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: "bold",
          fontFamily: "FiraSans, sans-serif",
          letterSpacing: "0.3px",
          boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
          textDecoration: "none",
          cursor: "pointer",
        }}
      >
        zur HUM-ID Webseite
      </a>

      {/* ─── Fixer Header ────────────────── */}
      <header className="header-fixed">
        <div className="header-inner">
          <h1>HUM‑ID Sensor-Planer</h1>
          <img
            src="https://www.hum-id.com/vertrieb/logowhite.png"
            alt="HUM‑ID Logo"
            className="humid-logo"
          />
          <div className="step-block">
            {step === 1 && (
              <>
                <p className="step-main">
                  <b>Schritt 1:</b> Bitte wählen Sie eine Grafik aus,
                  z.&nbsp;B.&nbsp;eine Dachaufsicht oder einen Grundriss.
                </p>
                <p className="step-info">
                  Format: <strong>PDF, PNG oder JPG.</strong> Hinweis: Ab einer
                  Dateigröße von 4&nbsp;MB wird der Plan komprimiert.
                </p>
              </>
            )}

            {step === 2 && (
              <>
                <p className="step-main">
                  <b>Schritt 2:</b> Legen Sie den Maßstab fest.
                </p>
                <p className="step-info">
                  Suchen Sie sich einen Maßstab, klicken Sie mit der Maus auf
                  den Startpunkt und auf den Endpunkt des Maßstabs und nennen
                  Sie den Abstand.
                </p>
              </>
            )}

            {step === 3 && (
              <>
                <p className="step-main">
                  <b>Schritt 3:</b> Definieren Sie die Fläche, auf der die
                  Sensoren verlegt werden sollen.
                </p>
                <p className="step-info">
                  Erstellen Sie per Mausklicks ein Polygon, sobald Sie wieder
                  auf den ersten&nbsp;Punkt klicken, ist die Fläche geschlossen
                  und färbt sich grün.
                </p>
              </>
            )}

            {step === 4 && (
              <>
                <p className="step-main">
                  <b>Schritt 4:</b> Wählen Sie ein Sensor-Raster aus.
                </p>
                <p className="step-info">
                  Für kleine Flächen bis 200m² empfehlen wir ein 1x1m² Raster,
                  für mittlere Flächen ein 1x2m² Raster, bei sehr große Flächen
                  über 1.000m² empfiehlt sich ein 1x4m² Raster.
                </p>
              </>
            )}

            {step === 5 && (
              <>
                <p className="step-main step-main-with-info">
                  <b>Schritt 5:</b> Detailanpassungen und Download.
                  <span
                    className="step5-info-icon-wrapper"
                    onClick={() => setShowStep5Info((v) => !v)}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM11 15h2v2h-2v-2zm0-8h2v6h-2V7z"
                      />
                    </svg>
                    {showStep5Info && (
                      <div className="step5-info-popover">
                        Wenn Sie das gesamte Grid verschieben möchten, wählen
                        Sie zunächst "Raster verschieben", klicken und halten
                        Sie dann einen Punkt des Rasters und verschieben Sie es.
                        Sie können auch Bereiche mit Sensorpunkten löschen (z.B.
                        bei Dachfenstern oder Photovoltaikpanelen) oder einzelne
                        neue Sensorpunkte hinzufügen. Im "Auswahl
                        verschieben"-Modus können Sie per Rechtsklick Sensoren
                        kopieren, drehen oder löschen.
                      </div>
                    )}
                  </span>
                </p>
              </>
            )}
          </div>
        </div>

        <nav className="action-bar">
          <div className="action-inner">
            <div className="actions">
              {/* === Step 1 ================== */}
              {step === 1 && (
                <>
                  <label className="file-label">
                    <svg
                      className="upload-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M12 16V4"
                        stroke="#0A2348"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                      <path
                        d="M6 10l6-6 6 6"
                        stroke="#0A2348"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M4 20h16"
                        stroke="#0A2348"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                    Datei auswählen
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={handleFileUpload}
                      className="file-input"
                    />
                  </label>
                  <span className="file-name">
                    {fileName || "Keine ausgewählt"}
                  </span>
                  <Btn
                    onClick={() => setStep(2)}
                    disabled={!imageObj}
                    variant="next"
                  >
                    Weiter
                  </Btn>
                </>
              )}

              {/* === Step 2 ======================= */}
              {step === 2 && (
                <>
                  <Btn onClick={() => setStep(1)}>
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                  </Btn>
                  {scalePoints.length === 4 && (
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Distanz (m)"
                      value={realDistance}
                      onChange={(e) => setRealDistance(e.target.value)}
                      className="distance-input"
                    />
                  )}
                  <Btn
                    onClick={undoScalePoint}
                    disabled={scalePoints.length === 0}
                  >
                    Undo
                  </Btn>
                  <Btn
                    onClick={applyScale}
                    disabled={!realDistance}
                    variant="next"
                  >
                    Maßstab übernehmen
                  </Btn>
                </>
              )}

              {/* === Step 3 ======================= */}
              {step === 3 && (
                <>
                  <Btn onClick={() => setStep(2)}>
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                  </Btn>
                  <Btn
                    onClick={undoLast}
                    disabled={polygonPoints.length === 0 || polyClosed}
                  >
                    Rückgängig
                  </Btn>
                  <Btn
                    onClick={resetPoly}
                    disabled={polygonPoints.length === 0}
                  >
                    Zurücksetzen
                  </Btn>
                  <Btn
                    onClick={finalizePoly}
                    disabled={!polyClosed}
                    variant="next"
                  >
                    Sensorfläche übernehmen
                  </Btn>
                  {polygonPoints.length === 0 && (
                    <span className="action-bar-hint">
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ marginRight: "8px" }}
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM11 15h2v2h-2v-2zm0-8h2v6h-2V7z"
                        />
                      </svg>{" "}
                      Erstellen Sie durch Klicks in der Karte eine geschlossene
                      Fläche
                    </span>
                  )}
                </>
              )}

              {/* === Step 4 ================= */}
              {step === 4 && (
                <>
                  <Btn onClick={() => setStep(3)}>
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                  </Btn>
                  <select
                    value={sensorRaster}
                    onChange={(e) => setSensorRaster(e.target.value)}
                    className="select-raster"
                  >
                    <option value="1">1 Sensor / m²</option>
                    <option value="2">1 Sensor / 2 m²</option>
                    <option value="4">1 Sensor / 4 m²</option>
                  </select>
                  <Btn onClick={() => setStep(5)} variant="next">
                    Weiter
                  </Btn>
                </>
              )}

              {/* === Step 5 ====================== */}
              {step === 5 && (
                <>
                  <Btn
                    onClick={() => setStep(4)}
                    disabled={
                      areaDeleteMode ||
                      addMode ||
                      rasterDraggable ||
                      moveSelectionMode ||
                      auraMode
                    }
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                  </Btn>
                  <div className="info-wrapper">
                    <Btn
                      onClick={toggleRasterDrag}
                      active={rasterDraggable}
                      disabled={
                        areaDeleteMode ||
                        addMode ||
                        moveSelectionMode ||
                        auraMode
                      }
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ marginRight: "8px" }}
                      >
                        <line x1="12" y1="2" x2="12" y2="22"></line>
                        <line x1="2" y1="12" x2="22" y2="12"></line>
                        <polyline points="19 9 12 2 5 9"></polyline>
                        <polyline points="19 15 12 22 5 15"></polyline>
                        <polyline points="9 5 2 12 9 19"></polyline>
                        <polyline points="15 5 22 12 15 19"></polyline>
                      </svg>
                      {rasterDraggable
                        ? "Raster fixieren"
                        : "Raster verschieben"}
                    </Btn>
                    <span className="tooltip">
                      Klicken und halten Sie einen Sensor im Raster, lassen Sie
                      los, um das gesamte Sensorraster zu platzieren.
                    </span>
                  </div>
                  <div className="info-wrapper">
                    <Btn
                      onClick={() => {
                        const newAreaDeleteMode = !areaDeleteMode;
                        setAreaDeleteMode(newAreaDeleteMode);
                        setSelRect(null);
                        if (newAreaDeleteMode) {
                          setRasterDraggable(false);
                          setAddMode(false);
                          setMoveSelectionMode(false);
                          setSelectedIds([]);
                          setAuraMode(false);
                        }
                      }}
                      active={areaDeleteMode}
                      disabled={
                        rasterDraggable ||
                        addMode ||
                        moveSelectionMode ||
                        auraMode
                      }
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ marginRight: "8px" }}
                      >
                        <circle cx="6" cy="6" r="3"></circle>
                        <circle cx="6" cy="18" r="3"></circle>
                        <line x1="20" y1="4" x2="8.12" y2="15.88"></line>
                        <line x1="14.47" y1="14.48" x2="20" y2="20"></line>
                        <line x1="8.12" y1="8.12" x2="12" y2="12"></line>
                      </svg>
                      {areaDeleteMode
                        ? "Bereich-Modus beenden"
                        : "Bereich löschen"}
                    </Btn>
                    <span className="tooltip">
                      Zeichnen Sie mit der Maus ein Rechteck um die Sensoren,
                      die Sie löschen möchten (z.B. bei Dachfenstern oder
                      Photovoltaikanlagen).
                    </span>
                  </div>
                  {/* === Auswahl verschieben ========================= */}
                  <div className="info-wrapper">
                    <Btn
                      onClick={() => {
                        const next = !moveSelectionMode;
                        setMoveSelectionMode(next);
                        setSelectedIds([]);
                        setSelRect(null);
                        // Beim Deaktivieren auch Dreh-/Paste-Modus beenden
                        setRotateMode(false);
                        setRotateAngle(0);
                        setRotateCenter(null);
                        setPasteMode(false);
                        setContextMenu(null);
                        if (next) {
                          setRasterDraggable(false);
                          setAreaDeleteMode(false);
                          setAddMode(false);
                          setAuraMode(false);
                        }
                      }}
                      active={moveSelectionMode}
                      disabled={
                        rasterDraggable || addMode || areaDeleteMode || auraMode
                      }
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ marginRight: "8px" }}
                      >
                        <rect
                          x="3"
                          y="3"
                          width="8"
                          height="8"
                          rx="1"
                          strokeDasharray="2 2"
                        ></rect>
                        <polyline points="14 7 17 7 17 10"></polyline>
                        <polyline points="21 14 21 17 18 17"></polyline>
                        <line x1="13" y1="13" x2="20" y2="20"></line>
                        <polyline points="20 20 17 20 20 17"></polyline>
                      </svg>
                      {moveSelectionMode
                        ? "Auswahl-Modus beenden"
                        : "Auswahl verschieben"}
                    </Btn>
                    <span className="tooltip">
                      Ziehen Sie ein Rechteck um Sensoren. Rechtsklick für
                      Kopieren, Drehen oder Löschen. Verschieben per Drag.
                    </span>
                  </div>
                  <div className="info-wrapper">
                    <Btn
                      onClick={() => {
                        const newAddMode = !addMode;
                        setAddMode(newAddMode);
                        if (newAddMode) {
                          setRasterDraggable(false);
                          setAreaDeleteMode(false);
                          setSelRect(null);
                          setMoveSelectionMode(false);
                          setSelectedIds([]);
                          setAuraMode(false);
                        }
                      }}
                      active={addMode}
                      disabled={
                        areaDeleteMode ||
                        rasterDraggable ||
                        moveSelectionMode ||
                        auraMode
                      }
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ marginRight: "8px" }}
                      >
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="16"></line>
                        <line x1="8" y1="12" x2="16" y2="12"></line>
                      </svg>
                      {addMode
                        ? "Hinzufügemodus beenden"
                        : "Sensoren hinzufügen"}
                    </Btn>
                    <span className="tooltip">
                      Fügen Sie einzelne Sensoren per Mausklick hinzu.
                    </span>
                  </div>
                  {/* === Aura ========================= */}
                  {AURA_FEATURE_ENABLED && (
                    <div className="info-wrapper">
                      <Btn
                        onClick={() => {
                          const next = !auraMode;
                          setAuraMode(next);
                          if (next) {
                            setRasterDraggable(false);
                            setAreaDeleteMode(false);
                            setSelRect(null);
                            setAddMode(false);
                            setMoveSelectionMode(false);
                            setSelectedIds([]);
                          }
                        }}
                        disabled={
                          rasterDraggable ||
                          addMode ||
                          areaDeleteMode ||
                          moveSelectionMode
                        }
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ marginRight: "8px" }}
                        >
                          <circle cx="12" cy="12" r="3"></circle>
                          <circle cx="12" cy="12" r="7" opacity="0.6"></circle>
                          <circle cx="12" cy="12" r="11" opacity="0.3"></circle>
                        </svg>
                        {auraMode ? "Aura löschen" : "Aura hinzufügen"}
                      </Btn>
                      <span className="tooltip">
                        Klicken Sie in den Plan, um eine Aura (blaue Reichweite,
                        10 m Radius) zu setzen. Klick auf den blauen Mittelpunkt
                        einer bestehenden Aura löscht sie.
                      </span>
                    </div>
                  )}
                  <div className="info-wrapper">
                    <Btn
                      onClick={undoLastAction}
                      disabled={history.length === 0 || rasterDraggable}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width="18"
                        height="18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 5L8 9l4 4"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8 9h5a5 5 0 110 10h-1"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    </Btn>
                    <span className="tooltip">
                      Macht die letzte Aktion rückgängig.
                    </span>
                  </div>
                  <div className="info-wrapper">
                    <Btn
                      onClick={resetAppStates}
                      disabled={
                        areaDeleteMode ||
                        addMode ||
                        rasterDraggable ||
                        moveSelectionMode ||
                        auraMode
                      }
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ marginRight: "8px" }}
                      >
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="12" y1="18" x2="12" y2="12"></line>
                        <line x1="9" y1="15" x2="15" y2="15"></line>
                      </svg>
                      Neu starten
                    </Btn>
                    <span className="tooltip">
                      Laden Sie ein neues Bild hoch (Achtung: Ggf. müssen Sie
                      vorher den Browsercache löschen)
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </nav>
      </header>

      {/* ─── Content ─── */}
      <div className="container">
        <div className="content-wrapper">
          {step === 1 && (
            <div className="space-y-4 border p-6 bg-white rounded"></div>
          )}
          {step === 2 && (
            <div className="space-y-4 border p-6 bg-white rounded">
              <div
                ref={containerRef}
                className="relative w-full h-[70vh] overflow-hidden stage-wrapper"
              >
                <ZoomStage showSensors={false} />
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="space-y-4 border p-6 bg-white rounded">
              <div
                ref={containerRef}
                className="relative w-full h-[70vh] overflow-hidden stage-wrapper"
              >
                <ZoomStage showSensors={false} />
              </div>
            </div>
          )}
          {step === 4 && (
            <div className="space-y-4 border p-6 bg-white rounded"></div>
          )}
          {step === 5 && (
            <div className="space-y-4 border p-6 bg-white rounded">
              <div
                ref={containerRef}
                className="relative w-full h-[70vh] overflow-hidden stage-wrapper"
              >
                <ZoomStage showSensors={step === 5} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* +++ POLYGON HILFE-POPOVER +++ */}
      {showPolygonHelpPopover && (
        <div className="polygon-help-popover-overlay">
          <div className="polygon-help-popover-content">
            <h2>Fläche definieren</h2>
            <p>
              Erstellen Sie per Mausklicks eine Fläche. Sobald Sie wieder auf
              den ersten Punkt klicken, ist die Fläche geschlossen und färbt
              sich grün.
            </p>
            <div className="gif-container">
              <img
                src={`https://www.hum-id.com/new/polygonanimation.gif`}
                alt="Animation zum Zeichnen eines Polygons"
                className="polygon-gif"
              />
            </div>
            <button
              onClick={handleClosePolygonHelpPopover}
              className="polygon-help-cta-btn"
            >
              Alles klar
            </button>
            <button
              onClick={handleDontShowPolygonHelpAgain}
              className="polygon-help-dont-show-btn"
            >
              Nicht wieder anzeigen
            </button>
          </div>
        </div>
      )}

      {/* +++ ANGEBOTS-MODAL +++ */}
      {showOfferModal && (
        <div className="offer-modal-overlay">
          <div className="offer-modal-content">
            <button
              className="offer-modal-close-btn"
              onClick={handleCloseOfferModal}
              aria-label="Dialog schließen"
            >
              ×
            </button>
            <h2>Unverbindliches Angebot anfordern</h2>
            <p>
              Fordern Sie jetzt ein unverbindliches Angebot für die von Ihnen
              berechneten
              <strong>
                {" "}
                {sensorPoints.length +
                  addedPoints.length -
                  deletedIds.length}{" "}
                Sensoren{" "}
              </strong>
              an.
            </p>
            <a
              href="https://www.hum-id.com/sensorplaner/anfrage"
              target="_blank"
              rel="noopener noreferrer"
              className="offer-modal-cta-btn"
            >
              Angebot anfordern
            </a>
          </div>
        </div>
      )}

      {/* ─── Kontextmenü (Rechtsklick) ─── */}
      {contextMenu && (
        <>
          <div
            className="context-menu-overlay"
            onClick={() => setContextMenu(null)}
          />
          <div
            className="context-menu"
            style={{
              top: contextMenu.screenY,
              left: contextMenu.screenX,
            }}
          >
            <button onClick={handleCopy}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="9" y="9" width="13" height="13" rx="2" />
                <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
              </svg>
              Kopieren
            </button>
            <button onClick={handleRotateStart}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M21 2v6h-6" />
                <path d="M3 12a9 9 0 0115-6.7L21 8" />
                <path d="M3 22v-6h6" />
                <path d="M21 12a9 9 0 01-15 6.7L3 16" />
              </svg>
              Drehen
            </button>
            <div className="context-menu-divider" />
            <button
              onClick={handleDeleteSelected}
              className="context-menu-delete"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M3 6h18" />
                <path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
              </svg>
              Löschen
            </button>
          </div>
        </>
      )}

      {/* ─── Paste-Modus Hinweis ─── */}
      {pasteMode && (
        <div className="paste-hint">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <rect x="9" y="9" width="13" height="13" rx="2" />
            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
          </svg>
          Rechtsklick auf den Plan, um die kopierten Sensoren einzufügen.
          <button
            onClick={() => {
              setPasteMode(false);
              setClipboard(null);
            }}
          >
            Abbrechen
          </button>
        </div>
      )}

      {/* ─── Dreh-Steuerung (Panel unten, über der Info-Bar) ─── */}
      {rotateMode && (
        <div className="rotate-panel">
          <span className="rotate-panel-label">Auswahl drehen:</span>
          <button
            className="rotate-panel-btn"
            onClick={() => setRotateAngle((a) => Math.max(-180, a - 15))}
          >
            −15°
          </button>
          <button
            className="rotate-panel-btn"
            onClick={() => setRotateAngle((a) => Math.max(-180, a - 1))}
          >
            −1°
          </button>
          <input
            type="number"
            className="rotate-panel-input"
            min={-180}
            max={180}
            value={rotateAngle}
            onChange={(e) => {
              let v = parseInt(e.target.value, 10);
              if (isNaN(v)) v = 0;
              v = Math.max(-180, Math.min(180, v));
              setRotateAngle(v);
            }}
          />
          <span className="rotate-panel-deg">°</span>
          <button
            className="rotate-panel-btn"
            onClick={() => setRotateAngle((a) => Math.min(180, a + 1))}
          >
            +1°
          </button>
          <button
            className="rotate-panel-btn"
            onClick={() => setRotateAngle((a) => Math.min(180, a + 15))}
          >
            +15°
          </button>
          <button className="rotate-panel-apply" onClick={applyRotation}>
            Übernehmen
          </button>
          <button
            className="rotate-panel-cancel"
            onClick={() => {
              setRotateMode(false);
              setRotateAngle(0);
              setRotateCenter(null);
            }}
          >
            Abbrechen
          </button>
        </div>
      )}

      {/* ─── Info-Leiste fix am unteren Bildschirmrand (nur Schritt 5) ─── */}
      {step === 5 && (
        <div className="step5-infobar-fixed">
          <span>
            <strong>Quadratmeter der Sensorfläche:</strong> {areaM2?.toFixed(1)}{" "}
            m²
          </span>
          <span className="step5-infobar-sep">|</span>
          <span>
            <strong>HUM-ID Sensoren:</strong>{" "}
            {sensorPoints.length + addedPoints.length - deletedIds.length}
          </span>
          {AURA_FEATURE_ENABLED && (
            <>
              <span className="step5-infobar-sep">|</span>
              <span>
                <strong>Aktive HUM-ID Auras:</strong> {auraPoints.length}
              </span>
            </>
          )}
          <button
            className="step5-infobar-download"
            onClick={handleDownload}
            disabled={
              areaDeleteMode ||
              addMode ||
              rasterDraggable ||
              moveSelectionMode ||
              auraMode
            }
          >
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none">
              <path
                d="M12 3v12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <path
                d="M8 13l4 4 4-4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M4 21h16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            Download PDF
          </button>
        </div>
      )}
    </>
  );
}
